import { isAxiosError } from "axios";
import { toast } from "sonner";
import { ApiError } from "@/services/api.service";

const extractApiMessage = (data: unknown): string | undefined => {
  if (!data || typeof data !== 'object') return undefined;
  const d = data as { message?: string; errors?: Record<string, string[]> };
  if (d.errors) {
    const first = Object.values(d.errors).flat()[0];
    if (first) return first;
  }
  return d.message;
};

const NETWORK_ERROR_MESSAGE = "Couldn't reach the server. Please try again later.";

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/**
 * onRetry is only offered a button on the network-error case -- a validation
 * or other real backend message means the request reached the server and
 * was rejected for a reason retrying won't fix; only a genuine connectivity
 * failure is worth a one-click "try that again".
 */
export const handleApiError = (error: unknown, onRetry?: () => void) => {
  if (error instanceof ApiError) {
    const msg = extractApiMessage(error.data);
    if (msg) {
      toast.error(msg);
      return;
    }
    if (error.isNetworkError) {
      if (onRetry) {
        toast.error(NETWORK_ERROR_MESSAGE, { action: { label: "Retry", onClick: onRetry } });
      } else {
        toast.error(NETWORK_ERROR_MESSAGE);
      }
      return;
    }
  }

  if (isAxiosError(error)) {
    const msg = extractApiMessage(error.response?.data) || error.message;
    toast.error(msg);
    return;
  }

  if (error instanceof Error) {
    toast.error(error.message);
    return
  }

  if (typeof error === 'string') {
    toast.error(error);
    return
  }

  toast.error(GENERIC_ERROR_MESSAGE);
}

/**
 * AUT-549. handleApiError, except that a 5xx response never reaches the toast as text: with debug
 * mode on, a server error's message is the raw exception (SQL, connection and database names), and
 * a user should never read that. 4xx messages are still shown, so a 422 validation message reaches
 * the user as before. Network failures are unchanged.
 */
export const handleApiErrorHidingServerText = (error: unknown, onRetry?: () => void) => {
  if (error instanceof ApiError && !error.isNetworkError && error.status >= 500) {
    toast.error(GENERIC_ERROR_MESSAGE);
    return;
  }
  if (isAxiosError(error) && (error.response?.status ?? 0) >= 500) {
    toast.error(GENERIC_ERROR_MESSAGE);
    return;
  }
  handleApiError(error, onRetry);
};
